import React from 'react';
import Layout from '../layout/Layout';
import Section from '../ui/Section';
import PageHero from '../ui/PageHero';
import Button from '../ui/Button';
import Sparkle from '../ui/Sparkle';
import Eyebrow from '../ui/Eyebrow';
import Reveal from '../ui/Reveal';
import { CONTACT } from '../layout/siteData';

const METHODS = [
  { icon: 'mail', title: 'Send us an email', value: CONTACT.email, href: `mailto:${CONTACT.email}?subject=Website%20Contact`, tint: 'bg-sky' },
  { icon: 'spark', title: 'Use the live chat', value: 'We usually reply in minutes', href: '#enquiry', tint: 'bg-cream' },
];

const FAQS = [
  ['Can I cancel my account at any time?', 'Of course! You subscribe on a month-to-month basis. No long-term contracts are involved.'],
  ['Can I change to a different billing plan at any time?', 'Our billing model is really flexible. You have full control over which packages you use — upgrade or downgrade at any time.'],
  ['What happens after my 14-day free trial ends?', 'Your store is placed on hold until you choose a package, at which point we reactivate it! Not sure which to choose? We’re always here to help.'],
  ['What are the promotion Terms & Conditions?', 'Your discounted subscription begins on the date you sign up and ends at midnight three calendar months later. You’re then billed the full price for your subscription.'],
];

const MethodIcon = ({ type }) => {
  if (type === 'mail') return <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="2.5" y="4.5" width="19" height="15" rx="3" stroke="currentColor" strokeWidth="2" /><path d="M3 7l9 6 9-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>;
  return <Sparkle size={22} />;
};

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-2">
    <span className="font-bold text-ink/80 text-[0.92rem]">{label}</span>
    {children}
  </label>
);

const inputCls = 'font-sans text-base text-ink px-4 py-3.5 border-[1.5px] border-line rounded-xl bg-cream focus:outline-none focus:border-brand focus:bg-white focus:ring-4 focus:ring-brand/10 transition resize-y';

const productOptionCls = [
  'flex items-center gap-3.5 px-4 py-3.5 border-[1.5px] border-line rounded-xl bg-cream cursor-pointer transition hover:border-brand',
  'has-[:checked]:border-ink has-[:checked]:bg-white has-[:focus-visible]:ring-4 has-[:focus-visible]:ring-brand/20',
].join(' ');

// The product picked decides which support inbox gets the enquiry (options and
// routing live in MarketingContactMessage). Each product shows its store address
// format so customers can match it to their own store.
const ProductChoice = ({ options }) => (
  <fieldset data-testid="contact-product" aria-describedby="contact-product-hint">
    <legend className="font-bold text-ink/80 text-[0.92rem]">Which Shopstar product do you use? *</legend>
    <p id="contact-product-hint" className="mt-1 text-sm text-ink/70">Not sure? Check your store’s web address.</p>
    <div className="mt-3 flex flex-col gap-2.5">
      {options.map((o) => (
        <label
          key={o.value}
          htmlFor={`contact-product-${o.value}`}
          data-testid={`contact-product-option-${o.value}`}
          className={productOptionCls}
        >
          <input
            type="radio"
            id={`contact-product-${o.value}`}
            name="contact_message[product]"
            value={o.value}
            required
            className="flex-none w-5 h-5 accent-ink cursor-pointer"
            data-testid={`contact-product-${o.value}`}
          />
          <span className="flex flex-col gap-1 min-w-0">
            <span className="font-bold text-ink">{o.label}</span>
            {o.address
              ? <code className="self-start font-mono text-[0.85rem] text-ink bg-sky rounded-md px-2 py-0.5 break-all">{o.address}</code>
              : <span className="text-sm text-ink/70">{o.hint}</span>}
          </span>
        </label>
      ))}
    </div>
  </fieldset>
);

const Contact = ({ sent = false, recaptchaHtml = '', productOptions = [] }) => (
  <Layout>
    <PageHero tint="peach" eyebrow="Contact" title="We’re here to help, at every step." lead="Reach us via email or live chat — we’d love to hear from you." />

    <Section tone="white" pad="md">
      <div className="grid md:grid-cols-2 gap-5">
        {METHODS.map((m, i) => (
          <Reveal key={m.title} delay={i * 70}>
            <a href={m.href} className={`block ${m.tint} rounded-4xl p-8 hover:-translate-y-1 hover:shadow-soft transition-all`}>
              <span className="inline-grid place-items-center w-[52px] h-[52px] rounded-2xl bg-white text-ink mb-5"><MethodIcon type={m.icon} /></span>
              <strong className="block text-h3 font-extrabold text-ink">{m.title}</strong>
              <span className="block text-ink font-semibold mt-1.5">{m.value}</span>
            </a>
          </Reveal>
        ))}
      </div>
    </Section>

    <Section tone="cream" pad="lg" id="enquiry">
      <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-12 items-start">
        <div className="lg:sticky lg:top-28">
          <Eyebrow>Send us an enquiry</Eyebrow>
          <h2 className="mt-4 text-h2 font-extrabold text-ink">Tell us what you need.</h2>
          <p className="mt-4 text-lead text-body max-w-[38ch]">Fill in the form and a real person from our 100% South African team will get back to you.</p>
        </div>
        <div className="bg-white border border-line rounded-4xl p-8 md:p-10 shadow-soft">
          {sent ? (
            <div className="text-center py-5">
              <Sparkle size={40} color="#95a8e6" className="mx-auto" />
              <h3 className="mt-4 mb-3 text-h3 font-extrabold text-ink">Thank you! Your message has been received.</h3>
              <p className="mb-6 text-body">We’ll be in touch shortly. In the meantime, why not start your free trial?</p>
              <Button href="https://app.shopstar.co.za/users/sign_up">Start for free</Button>
            </div>
          ) : (
            <form className="flex flex-col gap-5" method="post" action="/contact" data-testid="contact-form">
              <ProductChoice options={productOptions} />
              <Field label="Your name *"><input type="text" name="contact_message[name]" required autoComplete="name" className={inputCls} /></Field>
              <Field label="Email address *"><input type="email" name="contact_message[email]" required autoComplete="email" className={inputCls} /></Field>
              <Field label="How can we help? *"><textarea name="contact_message[body]" rows="5" required className={inputCls} /></Field>
              {recaptchaHtml && <div dangerouslySetInnerHTML={{ __html: recaptchaHtml }} />}
              <Button icon={false} full type="submit">Send enquiry</Button>
            </form>
          )}
        </div>
      </div>
    </Section>

    <Section tone="white" pad="lg">
      <div className="max-w-[40ch] mb-10"><Eyebrow>Frequently asked</Eyebrow><h2 className="mt-4 text-h2 font-extrabold text-ink">Frequently asked questions.</h2></div>
      <div className="max-w-[820px]">
        {FAQS.map(([q, a]) => (
          <details key={q} className="group border-b border-line py-2">
            <summary className="flex items-center justify-between gap-5 cursor-pointer list-none py-5 text-lg font-bold text-ink [&::-webkit-details-marker]:hidden">
              {q}
              <span className="text-ink text-2xl font-light flex-none transition-transform group-open:rotate-45">+</span>
            </summary>
            <p className="pb-6 pr-1 text-body max-w-[70ch]">{a}</p>
          </details>
        ))}
      </div>
    </Section>
  </Layout>
);

export default Contact;
